/**
 * PhasePlate - the session mode in the header.
 *
 * THE MODE GROUP of the header (v9.6.0). Whoever directs the table (the GM,
 * or anyone outside a session) gets three lit plates: one press moves
 * everyone. The eye beside them is the PEEK - while it is on, a plate
 * changes only this screen, and a line says which mode the table is really
 * in. Everyone else sees the mode's name in the same place, without the
 * buttons. In a phase that wears the danger, its name follows for
 * everyone; for the director it is a word that opens the danger's pin.
 *
 * THE TABLE GROUP follows for the director: one word, Table, opening who
 * the party is, whether positions count and - where the mode can break
 * into a fight - Start the fight (`SidesControl`).
 *
 * Draws nothing until the phase is known (see hooks/useDirection).
 */
import styles from '../styles/PhasePlate.module.css';
import { PHASES } from '../engine/phase/types';
import { PHASE_LABELS } from '../engine/phase/frame';
import { layoutOf, positionsCount } from '../engine/phase/layout';
import { DANGER_LABELS } from '../engine/phase/danger';
import SidesControl, { DangerPlates } from './SidesControl';
import HeaderMenu from './header/HeaderMenu';
import StartFight from './StartFight';

/** The three plates. A press moves the table, or only this screen while peeking. */
function Plates({ direction }) {
  const { shown, peeking, setPhase, look } = direction;
  const press = peeking ? look : setPhase;
  return (
    <div className={styles.plate} role="group" aria-label="Session mode" data-testid="phase-plate">
      {PHASES.map((phase) => (
        <button
          key={phase}
          type="button"
          className={styles.option}
          aria-pressed={phase === shown}
          data-testid={`phase-${phase}`}
          onClick={() => press(phase)}
        >
          {PHASE_LABELS[phase]}
        </button>
      ))}
    </div>
  );
}

/** Whether positions count at the table, and whether that is the GM's word or the mode's default. */
function positionsOf(frame) {
  if (!frame) return null;
  return { count: positionsCount(frame), overridden: frame.positions !== null };
}

/** How bad the fight is, in words - for everyone, while the phase on screen wears the danger. */
function DangerLabel({ shown, danger }) {
  if (!danger || !layoutOf(shown).wearsDanger) return null;
  return (
    <span className={styles.danger} data-testid="danger-label">
      {DANGER_LABELS[danger.step]}
    </span>
  );
}

/** The same word for whoever directs: it opens the pin (four plates, and Follow the numbers). */
function DangerControl({ shown, danger, onDirect }) {
  if (!danger || !layoutOf(shown).wearsDanger) return null;
  return (
    <HeaderMenu
      label={DANGER_LABELS[danger.step]}
      className={styles.dangerToggle}
      testId="danger-label"
      panelTestId="danger-panel"
      ariaLabel="The danger"
      title="How bad the fight is. Open to pin it, or to follow the numbers"
    >
      <DangerPlates danger={danger} onDirect={onDirect} />
    </HeaderMenu>
  );
}

/** The peek: an eye on the plates. While it is lit, a plate changes only this screen. */
function LookSwitch({ peeking, shown, look }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={peeking}
      aria-label="Look only"
      className={styles.eye}
      data-testid="phase-look"
      title="Look only: see another mode on this screen without moving the table"
      onClick={() => look(peeking ? null : shown)}
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        aria-hidden="true"
      >
        <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7-10-7-10-7z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    </button>
  );
}

/**
 * @param {object} props
 * @param {import('../hooks/useDirection').Direction} props.direction
 * @param {boolean} props.canDirect - this viewer may move the table
 * @param {import('../engine/phase/danger').DangerReading} [props.danger]
 * @param {{tag: string, label: string}[]} [props.teams] - the teams on the roster
 * @param {import('../engine/phase/sides').Sides} [props.fightSides]
 * @param {(plan: object) => void} [props.onStartFight] - offered where the table's phase can break into a fight
 */
export default function PhasePlate({ direction, canDirect, danger, teams = [], fightSides, onStartFight }) {
  const { phase, shown, peeking, look } = direction;
  if (!phase) return null;
  if (!canDirect) {
    return (
      <div className={styles.wrap}>
        <span className={styles.name} data-testid="phase-name">
          {PHASE_LABELS[phase]}
        </span>
        <DangerLabel shown={shown} danger={danger} />
      </div>
    );
  }
  const startsFight = onStartFight && fightSides && layoutOf(phase).offersFight;
  return (
    <div className={styles.wrap}>
      <Plates direction={direction} />
      <LookSwitch peeking={peeking} shown={shown} look={look} />
      <DangerControl shown={shown} danger={danger} onDirect={direction.direct} />
      {peeking && (
        <span className={styles.peeking} data-testid="phase-peeking" role="status">
          Peeking: the table is in {PHASE_LABELS[phase]}
        </span>
      )}
      <span className={styles.rule} aria-hidden="true" />
      {danger && (
        <SidesControl
          danger={danger}
          teams={teams}
          partyTeam={direction.frame?.partyTeam ?? null}
          positions={positionsOf(direction.frame)}
          onDirect={direction.direct}
        >
          {startsFight && <StartFight sides={fightSides} onStart={onStartFight} className={styles.fight} />}
        </SidesControl>
      )}
    </div>
  );
}
